// «مطب خودم را ثبت می‌کنم» — what the screen SENDS and what it SAYS back, kept
// pure so it can be pinned without mounting a screen whose logo is a Parcel
// `url:` import (vite cannot resolve that scheme, which is why no test mounts
// index.jsx).

import {
  CURRENCY_CODES, DEFAULT_CURRENCY, isClinicCurrency, unitLabel,
} from '../../lib/money.js';

/**
 * A currency as the picker names it: «درهم امارات (AED)» / "UAE dirham (AED)".
 * IRR is this app's own word — «تومان (IRR)» — because an Iranian clinic reads
 * its money in toman, and the rial is only what the wire counts.
 */
export function currencyOptionLabel(code, { t, locale }) {
  if (code === DEFAULT_CURRENCY) return t('onboarding.clinic.currencyIrr');
  return `${unitLabel(code, locale, 'long')} (${code})`;
}

/** The platform's closed list, IRR first — the framework's, never a copy. */
export function currencyOptions({ t, locale }) {
  return CURRENCY_CODES.map((code) => ({ value: code, label: currencyOptionLabel(code, { t, locale }) }));
}

/**
 * The CreateOwnClinicInput for a practice the dentist owns.
 *
 * OFFICE, not CLINIC: this branch is explicitly "my own practice"; a shared
 * clinic is the one you JOIN with a code. `officePhone` is already E.164 (or '')
 * — PhoneInput emits nothing else. `currency` is ALWAYS sent, IRR included: it
 * is the dentist's answer, and the BFF refuses anything off the closed list
 * before writing a row.
 */
export function ownClinicInput({ name, city, address, officePhone, currency }) {
  return {
    name: String(name || '').trim(),
    clinicType: 'OFFICE',
    city: String(city || '').trim() || undefined,
    address: String(address || '').trim() || undefined,
    officePhone: officePhone || undefined,
    currency: currency || DEFAULT_CURRENCY,
  };
}

/**
 * What a refused «ثبت مطب» says: `{ message, blocked }`.
 *
 *   CLINIC_EXISTS            not really a failure: the resolver adopts an
 *                            existing practice rather than creating a second
 *                            one, so this only surfaces if adopting failed.
 *   CURRENCY_MISMATCH        the account already has a practice, in another
 *                            currency (`extensions.currency` is its own). The
 *                            BFF refuses rather than hand over a practice whose
 *                            every figure is in a unit the dentist did not pick.
 *   CLINIC_SETUP_INCOMPLETE  the practice row exists but accounting never got
 *                            its currency. `blocked`: the BFF can no longer find
 *                            a uuid-keyed practice by the account's id, so a
 *                            retry would register a SECOND practice.
 */
export function ownClinicFailure(err, { t, locale }) {
  const code = err?.extensions?.code || null;
  if (code === 'CLINIC_EXISTS') {
    return { message: t('onboarding.clinic.alreadyExists'), blocked: false };
  }
  if (code === 'CURRENCY_MISMATCH') {
    const theirs = err?.extensions?.currency;
    return {
      message: t('onboarding.clinic.currencyMismatch', {
        currency: isClinicCurrency(theirs)
          ? currencyOptionLabel(theirs, { t, locale })
          : String(theirs || '—'),
      }),
      blocked: false,
    };
  }
  if (code === 'CLINIC_SETUP_INCOMPLETE') {
    return { message: t('onboarding.clinic.setupIncomplete'), blocked: true };
  }
  return { message: t('errors.generic'), blocked: false };
}
